import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { loginCallApi } from "../../data/userData";
import Swal from "sweetalert2";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import money from "../../Assets/Images/business.jpg";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { savePendingAuth } from "./authStorage";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { companyManagementUrl } from "../../config"; // TODO: confirm this is the right service url for companySettings

function saveTokenToStorage(decodedToken) {
  localStorage.setItem("decodedToken", JSON.stringify(decodedToken));
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ── OLD FLOW: commits the session to localStorage immediately, no OTP ──────
async function saveDataToLocalStorage(response) {
  const token = response.data.token;
  localStorage.setItem("token", JSON.stringify(token));

  const serverLogoPath = response.data.companyLogoPath
    ? `${response.data.companyLogoPath}`
    : null;

  // If the user has already uploaded a custom logo (saved as a DataURL),
  // NEVER overwrite it with the server's old/default companyLogoPath.
  const cachedDataUrl = localStorage.getItem("uploadedLogoDataUrl");
  const filePath =
    cachedDataUrl && cachedDataUrl !== "null" && cachedDataUrl !== "undefined"
      ? cachedDataUrl
      : serverLogoPath;
  localStorage.setItem("filePath", filePath);

  const decodedToken = jwtDecode(token);
  const expirationTime = decodedToken?.exp
    ? decodedToken.exp * 1000
    : new Date().getTime() + 5 * 60 * 60 * 1000;
  localStorage.setItem("tokenExpiration", expirationTime);
  localStorage.setItem("rights", JSON.stringify(response.data.rights));
  saveTokenToStorage(decodedToken);
}

// ── "Use OTP for Login" company setting (fail-CLOSED) ──────────────────────
const OTP_SETTING_NAME = "Use OTP for Login";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Returns true/false ONLY when the setting was genuinely read.
// Throws on anything ambiguous (empty response, bad shape, network error).
async function readOtpSetting() {
  const response = await fetchJsonDynamic(
    "companySettings",
    null,
    null,
    companyManagementUrl.uri
  );

  if (!response || response.results == null) {
    throw new Error("Empty settings response");
  }

  const settings =
    typeof response.results === "string"
      ? JSON.parse(response.results)
      : response.results;

  // An empty/invalid list is suspicious, so treat it as "unknown", not "off"
  if (!Array.isArray(settings) || settings.length === 0) {
    throw new Error("Unexpected settings payload");
  }

  const row = settings.find((s) => s.Setting === OTP_SETTING_NAME);
  return ["true", "1", "yes"].includes(
    String(row?.Value ?? "").trim().toLowerCase()
  );
}

// The company-settings call is authenticated: it only works with the token
// returned by a successful login (without it the server answers 401).
// The shared fetch helper reads the token from localStorage, but the real
// session must NOT be committed until OTP is verified. So the fresh token is
// staged for the duration of the call only, then the previous value (if any)
// is restored, whatever the outcome.
const TOKEN_STORAGE_KEY = "token";

async function withStagedToken(token, fn) {
  if (!token) throw new Error("Missing login token");
  const previous = localStorage.getItem(TOKEN_STORAGE_KEY);
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));
  try {
    return await fn();
  } finally {
    if (previous === null) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      localStorage.setItem(TOKEN_STORAGE_KEY, previous);
    }
  }
}

// Retries transient failures; throws if the setting still can't be read.
async function getOtpEnabled(token, retries = 3) {
  return withStagedToken(token, async () => {
    let lastError;
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        return await readOtpSetting();
      } catch (err) {
        lastError = err;
        console.error(`OTP setting read failed (attempt ${attempt + 1}):`, err);
        if (attempt < retries - 1) await sleep(400 * (attempt + 1));
      }
    }
    throw lastError;
  });
}

// ── "Pouring milk" loading animation (soft, translucent, professional) ─────
// Switch the loader between blue and green by changing ONE word below.
const LOADER_PALETTES = {
  blue: {
    liquidTop: "#B3E5FC",
    liquid: "#4FC3F7",
    liquidDeep: "#0288D1",
    glass: "#90CAF9",
    nozzleFill: "#E3EEF8",
    nozzleStroke: "#9DB7CF",
    title: "#0277BD",
    muted: "#5F6F81",
    overlay: "rgba(240, 247, 253, 0.68)",
    card: "rgba(255, 255, 255, 0.80)",
    shadow: "rgba(2, 60, 110, 0.16)",
  },
  green: {
    liquidTop: "#C8E6C9",
    liquid: "#66BB6A",
    liquidDeep: "#2E7D32",
    glass: "#A5D6A7",
    nozzleFill: "#E6F1E7",
    nozzleStroke: "#9DBFA0",
    title: "#2E7D32",
    muted: "#5F6F81",
    overlay: "rgba(243, 250, 244, 0.68)",
    card: "rgba(255, 255, 255, 0.80)",
    shadow: "rgba(20, 80, 30, 0.16)",
  },
};
const LOADER_THEME = LOADER_PALETTES.blue; // <- change to .green if preferred

const waveLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50px); }
`;
const waveRight = keyframes`
  from { transform: translateX(-50px); }
  to   { transform: translateX(0); }
`;
const flowDown = keyframes`
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -16; }
`;
const ripple = keyframes`
  0%   { transform: scale(0.3); opacity: 0.8; }
  100% { transform: scale(1.5); opacity: 0; }
`;
const bubbleRise = keyframes`
  0%   { transform: translateY(0); opacity: 0; }
  15%  { opacity: 0.8; }
  100% { transform: translateY(-110px); opacity: 0; }
`;
const fadeText = keyframes`
  0%   { opacity: 0; transform: translateY(4px); }
  15%  { opacity: 1; transform: translateY(0); }
  85%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-4px); }
`;
const cardIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const STAGE_MESSAGES = [
  "Verifying your credentials",
  "Securing your account",
  "Preparing your dashboard",
];
const reduceMotion = "@media (prefers-reduced-motion: reduce)";

// Tank geometry (SVG units) and the fill cycle (1 tick = 40ms)
const TANK = { x: 70, y: 90, w: 100, h: 140 };
const HOLD_START = 15; // drained, waiting to pour
const FILL_TICKS = 100; // 1% per tick
const HOLD_END = 20; // full, pause
const CYCLE_TICKS = HOLD_START + FILL_TICKS + HOLD_END;

function PourLoader({ message }) {
  const T = LOADER_THEME;
  const [stage, setStage] = React.useState(0);
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(
      () => setStage((st) => (st + 1) % STAGE_MESSAGES.length),
      2200
    );
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % CYCLE_TICKS), 40);
    return () => clearInterval(id);
  }, []);

  const percent = Math.min(100, Math.max(0, tick - HOLD_START));
  const pouring = tick >= HOLD_START && tick < HOLD_START + FILL_TICKS;
  const draining = tick < HOLD_START;
  // liquid surface (starts just below the tank so 0% shows nothing)
  const surfaceY = TANK.y + TANK.h + 6 - ((TANK.h + 6) * percent) / 100;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        background: T.overlay,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 260,
          maxWidth: "88vw",
          px: 5,
          pt: 3.5,
          pb: 3.5,
          borderRadius: 4,
          background: T.card,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.7)",
          boxShadow: `0 16px 48px ${T.shadow}`,
          animation: `${cardIn} 0.35s ease-out both`,
          [reduceMotion]: { animation: "none" },
        }}
      >
        <Box
          component="svg"
          viewBox="50 4 140 236"
          sx={{ width: { xs: 84, sm: 100 }, height: "auto" }}
        >
          <defs>
            <clipPath id="pourTankClip">
              <rect x={TANK.x} y={TANK.y} width={TANK.w} height={TANK.h} rx="20" />
            </clipPath>
            <linearGradient id="pourLiquid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={T.liquidTop} />
              <stop offset="40%" stopColor={T.liquid} />
              <stop offset="100%" stopColor={T.liquidDeep} />
            </linearGradient>
          </defs>

          {/* Glass tank */}
          <rect
            x={TANK.x}
            y={TANK.y}
            width={TANK.w}
            height={TANK.h}
            rx="20"
            fill="rgba(2, 136, 209, 0.05)"
          />

          {/* Liquid (clipped to the tank) */}
          <g clipPath="url(#pourTankClip)">
            <g
              style={{
                transform: `translate(${TANK.x}px, ${surfaceY}px)`,
                transition: draining
                  ? "transform 0.7s ease-in"
                  : "transform 0.06s linear",
              }}
            >
              {/* back wave */}
              <Box
                component="path"
                d="M0 8 Q12.5 0 25 8 T50 8 T75 8 T100 8 T125 8 T150 8 T175 8 T200 8 T225 8 T250 8 T275 8 T300 8 V170 H0 Z"
                fill="url(#pourLiquid)"
                fillOpacity="0.5"
                sx={{
                  animation: `${waveRight} 2.6s linear infinite`,
                  [reduceMotion]: { animation: "none" },
                }}
              />
              {/* front wave */}
              <Box
                component="path"
                d="M0 8 Q12.5 0 25 8 T50 8 T75 8 T100 8 T125 8 T150 8 T175 8 T200 8 T225 8 T250 8 T275 8 T300 8 V170 H0 Z"
                fill="url(#pourLiquid)"
                sx={{
                  animation: `${waveLeft} 1.7s linear infinite`,
                  [reduceMotion]: { animation: "none" },
                }}
              />
              {/* bubbles */}
              {[
                { cx: 22, delay: "0s", r: 2.5 },
                { cx: 50, delay: "0.9s", r: 3.2 },
                { cx: 76, delay: "1.7s", r: 2 },
              ].map((b, i) => (
                <Box
                  key={i}
                  component="circle"
                  cx={b.cx}
                  cy="130"
                  r={b.r}
                  fill="#fff"
                  fillOpacity="0.7"
                  sx={{
                    animation: `${bubbleRise} 2.4s ease-in infinite`,
                    animationDelay: b.delay,
                    [reduceMotion]: { animation: "none", opacity: 0 },
                  }}
                />
              ))}
            </g>
          </g>

          {/* Glass outline + soft highlight */}
          <rect
            x={TANK.x}
            y={TANK.y}
            width={TANK.w}
            height={TANK.h}
            rx="20"
            fill="none"
            stroke={T.glass}
            strokeWidth="3"
          />
          <line
            x1={TANK.x + 10}
            y1={TANK.y + 18}
            x2={TANK.x + 10}
            y2={TANK.y + 70}
            stroke="#fff"
            strokeOpacity="0.6"
            strokeWidth="3"
            strokeLinecap="round"
          />

          {/* Stream */}
          <g
            style={{
              opacity: pouring ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
          >
            <line
              x1="120"
              y1="30"
              x2="120"
              y2={surfaceY + 4}
              stroke={T.liquid}
              strokeOpacity="0.9"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <Box
              component="line"
              x1="120"
              y1="30"
              x2="120"
              y2={surfaceY + 4}
              stroke="#fff"
              strokeOpacity="0.75"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeDasharray="6 10"
              sx={{
                animation: `${flowDown} 0.45s linear infinite`,
                [reduceMotion]: { animation: "none" },
              }}
            />
            {/* soft ripple where the stream meets the surface */}
            <Box
              component="ellipse"
              cx="120"
              cy={surfaceY + 4}
              rx="16"
              ry="3.5"
              fill="none"
              stroke={T.liquidTop}
              strokeWidth="1.8"
              sx={{
                transformBox: "fill-box",
                transformOrigin: "center",
                animation: `${ripple} 1.1s ease-out infinite`,
                [reduceMotion]: { animation: "none", opacity: 0 },
              }}
            />
          </g>

          {/* Nozzle */}
          <rect
            x="98"
            y="14"
            width="44"
            height="16"
            rx="8"
            fill={T.nozzleFill}
            stroke={T.nozzleStroke}
            strokeWidth="2"
          />
        </Box>

        <Typography
          sx={{
            mt: 2.5,
            fontWeight: 600,
            color: T.title,
            textAlign: "center",
          }}
        >
          {message || "Signing you in..."}
        </Typography>
        <Typography
          key={stage}
          variant="body2"
          sx={{
            mt: 0.5,
            color: T.muted,
            minHeight: 22,
            textAlign: "center",
            animation: `${fadeText} 2.2s ease-in-out`,
            [reduceMotion]: { animation: "none" },
          }}
        >
          {STAGE_MESSAGES[stage]}...
        </Typography>
      </Box>
    </Box>
  );
}

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      align="center"
      {...props}
      sx={{ color: "text.secondary", mt: 4, ...props.sx }}
    >
      {"Copyright © "}
      <Link
        href="https://sababisha.com/"
        sx={{
          color: "#0288D1",
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline", color: "#0277BD" },
        }}
      >
        Sababisha Solutions Ltd
      </Link>{" "}
      {new Date().getFullYear()}. All rights reserved.
    </Typography>
  );
}

const defaultTheme = createTheme();

export default function LoginSide({ onLogin }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [loginAttempts, setLoginAttempts] = React.useState(3);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackSeverity, setSnackSeverity] = React.useState("warning");
  const [loadingMessage, setLoadingMessage] = React.useState("");

  React.useEffect(() => {
    const savedUsername = sessionStorage.getItem("username");
    const savedPassword = sessionStorage.getItem("password");
    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const handleUsernameChange = (e) => setUsername(e.target.value.trim());
  const handlePasswordChange = (e) => setPassword(e.target.value.trim());
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  // ── Where a logged-in user lands once the session is committed ─────────
  const routeAfterLogin = () => {
    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
    const roleGroupId = parseInt(decodedToken?.RoleGroupId, 10);

    onLogin();
    if (roleGroupId === 1) {
      navigate("/MCB-SWITCH");
    } else {
      navigate("/super-admin-dashboard");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setLoadingMessage("Signing you in...");

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    try {
      const response = await loginCallApi(
        trimmedUsername,
        trimmedPassword.replace(/\s/g, "")
      );

      if (response.code === "ERR_NETWORK") {
        Swal.fire({
          icon: "error",
          title: "Are You Still There?",
          text: "We couldn't connect. Please double-check your internet and give it another shot.",
        });
      }

      if (
        response.status === 502 ||
        response.status === 404 ||
        response.status === 503
      ) {
        Swal.fire({
          icon: "error",
          title: "This one is on us.",
          text: "Our servers are taking a coffee break ☕. Please try again in a few minutes.",
        });
      } else if (
        response.status === 500 ||
        response.status === 400 ||
        response.status === 401
      ) {
        Swal.fire({
          icon: "error",
          title: "Ooops! Something went wrong",
          text: response.response.data,
        });
      } else if (response.status === 200) {
        if (rememberMe) {
          sessionStorage.setItem("username", username);
          sessionStorage.setItem("password", password);
        } else {
          sessionStorage.removeItem("username");
          sessionStorage.removeItem("password");
        }

        setLoginAttempts(3);
        setSnackMessage("");

        // ------------------------------------------------------------ //
        // Check the OTP configuration AFTER successful authentication,
        // using the token from THIS login (the settings call needs it).
        // Fail CLOSED: if the setting cannot be confirmed, do not commit
        // a session (the token is only staged during the call, then removed).
        // ------------------------------------------------------------ //
        setLoadingMessage("Checking login configuration...");

        let otpEnabled;
        try {
          otpEnabled = await getOtpEnabled(response.data.token);
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: "Couldn't verify security settings",
            text: "We couldn't confirm your login configuration. Please try signing in again.",
            confirmButtonText: "OK",
          });
          return; // finally{} still resets loading
        }

        if (otpEnabled) {
          // ── NEW FLOW: hold the session until OTP is verified ──────────
          // Nothing goes into localStorage yet; OTPVerification commits
          // the real session once the code is confirmed.
          savePendingAuth({
            authType: "jwt",
            token: response.data.token,
            companyLogoPath: response.data.companyLogoPath,
            rights: response.data.rights,
            changepassword: response.data.changepassword === true,
          });
          navigate("/otp-verification");
          return;
        }

        // ------------------------------------------------------------ //
        // NORMAL LOGIN FLOW
        // ------------------------------------------------------------ //
        setLoadingMessage("Completing sign in...");

        // ── OLD FLOW: commit the session immediately ────────────────────
        if (response.data.changepassword === true) {
          await saveDataToLocalStorage(response);
          navigate("/changepassword");
          return;
        }

        await saveDataToLocalStorage(response);
        const rights = response.data.rights;
        const cleanedData = JSON.parse(rights.replace(/\\/g, ""));
        localStorage.setItem("rightsforrbac", cleanedData);

        routeAfterLogin();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops! Something went wrong",
        text: "Unable to login, check your internet connection and try again.",
        confirmButtonText: "OK, Got It",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      {loading && <PourLoader message={loadingMessage} />}
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            position: "relative",
            backgroundImage: `url(${money})`,
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(135, 206, 250, 0.15) 0%, rgba(102, 187, 106, 0.15) 100%)",
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          md={5}
          component={Paper}
          elevation={6}
          square
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          }}
        >
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                m: 1,
                bgcolor: "#4FC3F7",
                width: 60,
                height: 60,
                boxShadow: "0 4px 14px 0 rgba(79, 195, 247, 0.35)",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "#0277BD",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                marginBottom: 3,
              }}
            >
              Sign in to continue to your account
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Username"
                name="email"
                autoComplete="email"
                autoFocus
                value={username}
                onChange={handleUsernameChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    "&:hover fieldset": {
                      borderColor: "#4FC3F7",
                      borderWidth: 2,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0277BD",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0277BD",
                    fontWeight: 600,
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="HashedPassword"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="HashedPassword"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    "&:hover fieldset": {
                      borderColor: "#4FC3F7",
                      borderWidth: 2,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0277BD",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0277BD",
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                      sx={{
                        "&.Mui-checked": {
                          color: "primary.main",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Remember me
                    </Typography>
                  }
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  boxShadow: "0 4px 14px 0 rgba(79, 195, 247, 0.35)",
                  background: "linear-gradient(135deg, #4FC3F7 0%, #0288D1 100%)",
                  "&:hover": {
                    boxShadow: "0 6px 20px 0 rgba(79, 195, 247, 0.5)",
                    background: "linear-gradient(135deg, #29B6F6 0%, #0277BD 100%)",
                  },
                  "&:active": {
                    boxShadow: "0 2px 8px 0 rgba(79, 195, 247, 0.35)",
                  },
                  "&.Mui-disabled": {
                    background: "#cccccc",
                  },
                }}
              >
                Sign In
              </Button>
              <Snackbar
                open={snackOpen}
                autoHideDuration={4000}
                onClose={handleSnackClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={handleSnackClose}
                  severity={snackSeverity}
                  sx={{ width: "100%" }}
                >
                  {snackMessage}
                </Alert>
              </Snackbar>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Need help?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    href="/forgot-password"
                    variant="body2"
                    sx={{
                      color: "#0288D1",
                      textDecoration: "none",
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#0277BD",
                      },
                    }}
                  >
                    Forgot Password?
                  </Link>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    •
                  </Typography>
                  <Link
                    href="/unlock"
                    variant="body2"
                    sx={{
                      color: "#0288D1",
                      textDecoration: "none",
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#0277BD",
                      },
                    }}
                  >
                    Unlock Account
                  </Link>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, rgba(79,195,247,0.08) 0%, rgba(2,136,209,0.08) 100%)",
                  border: "1px solid rgba(79,195,247,0.2)",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  System under maintenance?{" "}
                  <Link
                    href="/dev-mode"
                    sx={{
                      color: "#0288D1",
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Play a game while you wait →
                  </Link>
                </Typography>
              </Box>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}